"use client";

import {
  CalendarClock,
  ChevronRight,
  CircleCheck,
  DoorOpen,
  Hourglass,
  Lock,
} from "lucide-react";
import { useRouter } from "next/navigation";
import { useEffect, useRef } from "react";
import { DetailInfoIcon } from "@/components/ui/detail-info-row";
import { useNow } from "@/hooks/use-now";
import {
  type BookingWindowIcon,
  type BookingWindowOccurrence,
  type BookingWindowState,
  type BookingWindowTone,
  resolveBookingWindowState,
} from "@/lib/events/booking-window-state";
import { cn } from "@/lib/utils";

interface BookingWindowRowProps {
  occurrence: BookingWindowOccurrence | null;
  /** Every scheduled occurrence, so a finished date can hand over to the next. */
  upcoming?: BookingWindowOccurrence[];
  onOpenSchedule: () => void;
  /**
   * Extra work to do when the clock crosses a moment the server's answer
   * depends on — an opening, a closing, a start. The row already refreshes the
   * route itself; this is for anything the route refresh cannot reach.
   */
  onBoundaryCross?: () => void;
  className?: string;
}

const ICONS: Record<
  Exclude<BookingWindowIcon, "live">,
  typeof CalendarClock
> = {
  hourglass: Hourglass,
  "calendar-clock": CalendarClock,
  "door-open": DoorOpen,
  lock: Lock,
  check: CircleCheck,
};

/**
 * Status is treatment, never a colour-washed row: the glass well carries the
 * signal (its tone) so the row stays as calm as the venue row above it. Amber
 * for a deadline, green for underway — no red, because a closing window is
 * not a failure.
 */
const HEADLINE_CLASSES: Record<BookingWindowTone, string> = {
  neutral: "text-text-primary dark:text-white",
  attention: "text-text-primary dark:text-white",
  live: "text-text-primary dark:text-white",
  muted: "text-text-secondary dark:text-white/60",
};

/**
 * The one live fact about getting into this event, and how soon.
 *
 * The hero above already says when the event is; this row exists to say what
 * happens next to your ability to book it. Everything it renders comes from
 * `resolveBookingWindowState`, which the booking footer reads too, so the two
 * cannot contradict each other.
 *
 * Nothing renders until mount: the copy depends on the viewer's clock and
 * timezone, and server-rendered HTML must not bake in the server's. The
 * placeholder holds the exact geometry of the real row, so the swap costs no
 * layout shift.
 */
export function BookingWindowRow({
  occurrence,
  upcoming,
  onOpenSchedule,
  onBoundaryCross,
  className,
}: BookingWindowRowProps) {
  // The ticker asks the state how long to wait, so a settled date schedules no
  // timer at all and a live countdown gets exactly the resolution it shows.
  const now = useNow((current) =>
    occurrence
      ? (resolveBookingWindowState({
          now: current,
          occurrence,
          upcoming,
        })?.tickMs ?? null)
      : null,
  );

  const state = now
    ? resolveBookingWindowState({ now, occurrence, upcoming })
    : null;

  const router = useRouter();
  useBoundaryCross(state, () => {
    // The row's own copy is clock-derived and already right. Whether booking is
    // actually possible is not: capacity, passes and eligibility are decided in
    // `getEventPageData`, so the server has to be asked again.
    router.refresh();
    onBoundaryCross?.();
  });

  if (!occurrence) return null;

  if (!state) {
    return (
      <div
        aria-hidden="true"
        className={cn(
          "flex w-full animate-pulse items-center gap-3",
          className,
        )}
      >
        <div className="size-11 shrink-0 rounded-[14px] bg-surface-alt dark:bg-white/[0.06]" />
        <div className="min-w-0 flex-1 space-y-2">
          <div className="h-4 w-40 rounded bg-surface-alt" />
          <div className="h-3 w-28 rounded bg-surface-alt" />
        </div>
        <div className="h-5 w-5 shrink-0" />
      </div>
    );
  }

  const Icon = state.icon === "live" ? null : ICONS[state.icon];

  return (
    <button
      type="button"
      onClick={onOpenSchedule}
      className={cn(
        "flex w-full items-center gap-3 rounded-2xl text-left transition-transform duration-150 ease-[var(--ease-out-strong)] active:scale-[0.99]",
        className,
      )}
    >
      <DetailInfoIcon
        icon={Icon ?? undefined}
        tone={state.tone}
        className="transition-colors duration-200 ease-[var(--ease-out-strong)]"
      >
        {Icon ? null : (
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inset-0 animate-ping rounded-full bg-success/60 motion-reduce:animate-none" />
            <span className="relative h-2.5 w-2.5 rounded-full bg-success" />
          </span>
        )}
      </DetailInfoIcon>

      {/* Keyed on the phase so a change of state crossfades, while the digits
          inside one phase change in place without animating. */}
      <span
        key={state.phase}
        className="min-w-0 flex-1 animate-in duration-150 ease-[var(--ease-out-strong)] fade-in-0 motion-reduce:animate-none"
      >
        <span
          className={cn(
            "block truncate text-sm font-medium tabular-nums",
            HEADLINE_CLASSES[state.tone],
          )}
        >
          {state.headline}
        </span>
        {state.subline ? (
          <span className="mt-0.5 block truncate text-xs text-text-secondary tabular-nums dark:text-white/55">
            {state.subline}
          </span>
        ) : (
          <span className="mt-0.5 block truncate text-xs text-text-secondary dark:text-white/55">
            View full schedule &amp; timeline
          </span>
        )}
      </span>

      <ChevronRight className="size-5 shrink-0 text-text-tertiary dark:text-white/40" />
    </button>
  );
}

/**
 * Fire `onBoundaryCross` once per phase or deadline change.
 *
 * A countdown reaching zero flips this row immediately, but whether booking is
 * actually possible is the server's call — capacity, passes and eligibility all
 * live there. Without this nudge the row would say the window had opened while
 * the button still said it had not. The first resolution after mount is not a
 * crossing: the page has only just been rendered by the server.
 */
function useBoundaryCross(
  state: BookingWindowState | null,
  onBoundaryCross: (() => void) | undefined,
) {
  const phaseKey = state
    ? `${state.occurrenceId}:${state.phase}:${state.target?.getTime() ?? "none"}`
    : null;
  const lastPhaseRef = useRef<string | null>(null);
  const callbackRef = useRef(onBoundaryCross);
  callbackRef.current = onBoundaryCross;

  useEffect(() => {
    if (!phaseKey) return;
    const previous = lastPhaseRef.current;
    lastPhaseRef.current = phaseKey;
    if (previous === null || previous === phaseKey) return;
    callbackRef.current?.();
  }, [phaseKey]);
}
